import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { FieldGroup } from "../../../../components";
import { Button, ButtonGroup, Icon, Segmented } from "../../../../components/ui";
import { SettingsContext } from "../../../../contexts";
import CompareSection from "./CompareSection";
import WhatsappMessageWithHighlighting from "./WhatsappMessageWithHighlighting";
import {
  boxOptions, yearOptions, monthOptions, conditionOptions,
  locationOptions, currencyOptions, paymentTypeOptions,
  conditionLabels, boxLabels, monthLabels,
} from "../../../../product/constants";
import { requestTypes } from "../filter/constants";
import { useFx } from "../../../../hooks/fx";
import NewPopup from "./NewPopup";
import { isEmpty } from "lodash";
import { useAlert } from "react-alert";
import { useApiError } from "../../../../hooks/useApiError.js";
import { getBaseCurrency, symbolFor } from "../../../../core/currency";

const getConditionLabel = (v) => v ? (conditionLabels[v] || v) : "N/A";
const getBoxLabel = (v) => v ? (boxLabels[v] || v) : "N/A";

const isMissing = (v) => {
  if (v === null || v === undefined) return true;
  if (typeof v === "number") return Number.isNaN(v) || v === 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "" || s === "n/a";
  }
  if (Array.isArray(v)) return v.length === 0;
  return false;
};

const fieldClass = (v) => `lm__field${isMissing(v) ? " lm__field--missing" : ""}`;

const WhatsappView = ({
  requestType, matchItem, productId, requestItem,
  fetchWhatsappData, isNonMatch, setIsRemoved, isRemoved,
  hideImport = false,
}) => {
  const [deliverySet, setDeliverySet] = useState(matchItem.delivery_set);
  const [year, setYear] = useState(matchItem.year * 1);
  const [month, setMonth] = useState(matchItem.month);
  const [condition, setCondition] = useState(matchItem.condition);
  const [currency, setCurrency] = useState(matchItem.currency);
  const [price, setPrice] = useState(matchItem.price);
  const [paymentType, setPaymentType] = useState(matchItem.payment_type);
  const [messageText, setMessageText] = useState(matchItem.message_text);
  const [location, setLocation] = useState(matchItem.region);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [request, setRequest] = useState(null);
  const [importLabel, setImportLabel] = useState("Import");
  const [approved, setApproved] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const [fetchedData, setFetchedData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [fetchedMatches, setFetchedMatches] = useState([{}]);
  const [activeItem, setActiveItem] = useState([]);
  const [compareLeftFormData, setCompareLeftFormData] = useState({});
  const [preGeneratedMessageText, setPreGeneratedMessageText] = useState("");
  const [activeTab, setActiveTab] = useState("original");
  const { settings } = useContext(SettingsContext);

  const GenerateInitialWAMessageText = async (matchItem) => {
    if (!matchItem) { await setPreGeneratedMessageText(""); return; }
    const senderName = matchItem.sender_name || "there";
    const groupName = matchItem.group_name || "the group";
    const matchedProductId = matchItem?.matched_data?.product_id;
    // The auto-reply offers OUR active listing of the matched product. With no
    // matched product, no active listing (the endpoint answers `{}` — Laravel
    // turns json(null) into an empty object), or a failed lookup there is
    // nothing to offer: leave the reply blank rather than throwing from an
    // un-awaited effect (Monitoring #1381, "reading 'case_material'").
    if (!matchedProductId) { setPreGeneratedMessageText(""); return; }
    let data;
    try {
      ({ data } = await axios.get(`/api/listings/matched-products/${matchedProductId}`));
    } catch {
      data = null;
    }
    if (!data?.id) { setPreGeneratedMessageText(""); return; }
    const brand = data.brand || "", model = data.model || "";
    const referenceNumber = data.reference_number || "";
    const caseMaterial = data.values?.case_material || "";
    const caseDiameter = data.values?.case_diameter || "";
    const dialColor = data.values?.dial_color || "";
    const formattedPrice = data.price ? Math.floor(data.price / 100).toLocaleString() : "0";
    const conditionLabel = getConditionLabel(data.condition);
    const boxLabel = getBoxLabel(data.box);
    const yearDisplay = data.month ? `${monthLabels[data.month]} ${data.year}` : data.year ? data.year.toString() : "";
    const listingLink = `${settings?.store_url || "https://app.retail-circle.io"}/product/${data.id}`;
    const message = `Hey ${senderName}, I've seen your message in ${groupName} group.\n\nWe have the watch below available if needed:\n\n\nBrand: ${brand} ${model}\nRef: ${referenceNumber}\nCase Material: ${caseMaterial}\nCase Size: ${caseDiameter}\nColor: ${dialColor}\n\n ▪️  ${symbolFor(data.currency ?? getBaseCurrency())}${formattedPrice}  ▪️\n\nDate: ${yearDisplay}\nCondition: ${conditionLabel}\nSet: ${boxLabel}\n\nPictures: ${listingLink}`;
    await setPreGeneratedMessageText(message);
  };

  const alert = useAlert();
  const { showApiError } = useApiError();
  const formData = { deliverySet, year, month, condition, currency, location, price };

  useEffect(() => {
    if (matchItem) {
      setDeliverySet(matchItem.delivery_set); setYear(matchItem.year * 1);
      setMonth(matchItem.month); setCondition(matchItem.condition);
      setCurrency(matchItem.currency); setPrice(matchItem.price);
      setPaymentType(matchItem.payment_type); setMessageText(matchItem.message_text);
      setLocation(matchItem.region); GenerateInitialWAMessageText(matchItem);
    }
  }, [matchItem]);

  // null = no known rate (request failed, nothing cached). max_budget is
  // saved in GBP, so it must never be written as rate × price with a guessed
  // rate: the two writers below refuse instead.
  const { rate } = useFx(currency || "gbp", "gbp");
  const fxBlocked = rate == null;
  const FX_BLOCKED_MSG =
    "Exchange rate unavailable — can't convert this price to GBP. Try again shortly.";
  // Same-origin proxies on the Laravel backend — see useTradeImage.js
  // for rationale (production CORS block on duckdns).
  const whatsappUrl = "/api/integrations/daowhatsapp";
  const marketUrl = "/api/integrations/daomarketplace";

  const sendPriceData = async (mi) => {
    try {
      await axios.post(`${whatsappUrl}/submit_price_to_dao`, {
        phone: mi.sender_phone, sender_name: mi.sender_name, message_id: mi.message_id,
        product_id: activeItem.product_id, currency, price: price * 100,
        condition: Array.isArray(condition) ? condition : [condition],
        box: deliverySet, location, year_month: month ? `${year} ${month}` : `${year}`,
        price_date: mi.message_date, is_non_match: isNonMatch,
      });
      setApproved(true);
      // Keep "Approved!" visible briefly before the parent removes this item from the list.
      setTimeout(() => { if (setIsRemoved) setIsRemoved(true); }, 1500);
    } catch (error) { showApiError(error, "send the WhatsApp message"); }
  };

  const sendApprovedData = async () => {
    try {
      await axios.post(`${whatsappUrl}/approve_request`, {
        request_id: requestItem.id, message_id: matchItem.message_id,
        product_id: activeItem.product_id, approved_by_id: requestItem.client_id,
        delivery_set: deliverySet, year, month,
        condition: Array.isArray(condition) ? condition : [condition],
        currency, region: location, price, payment_type: paymentType,
      });
    } catch (error) { console.log("error", error); }
  };

  const approveMessage = async () => {
    await axios.post(`/api/message-status`, { message_id: matchItem.message_id, product_id: activeItem.product_id });
  };

  const uploadImage = async () => {
    try { const { data } = await axios.post("/api/files", { url: matchItem.whatsapp_image_url }); return data.id; }
    catch { return 0; }
  };

  const updateOrderRequest = async () => {
    const fileId = await uploadImage();
    const { data } = await axios.put(`/api/requests/${requestItem.id}`, {
      product_id: activeItem.product_id, max_budget: Math.floor(rate * price * 100),
      message_id: matchItem.message_id, year, condition: [condition], type: requestItem?.type,
      request_type: requestType === requestTypes[0].id ? "sell" : "order",
      images: fileId ? [fileId] : [], delivery_set: deliverySet,
    });
    return data;
  };

  const onApprove = async () => {
    if (!isFetched) {
      if (fxBlocked && requestItem && requestType === requestTypes[1].id) {
        alert.error(FX_BLOCKED_MSG);
        return;
      }
      try {
        setIsLoading(true);
        await approveMessage();
        if (requestItem && requestType === requestTypes[1].id) { await sendApprovedData(); await updateOrderRequest(); }
        if (requestType === requestTypes[0].id) {
          const { data } = await axios.post("/api/users/search", {
            filters: {}, sort: { field: "created_at", direction: "desc" },
            query: matchItem.sender_phone, paging: { offset: 0, limit: 15 },
          });
          if (data.total > 0) { await sendPriceData(matchItem); }
          else {
            await axios.post("/api/users/pre-register", {
              type: "supplier", gender: "men", store_id: 16, owner_id: 1,
              first_name: matchItem.sender_name, last_name: matchItem.sender_name,
              phone: matchItem.sender_phone, email: "",
              address: { country_id: "", line1: "", line2: "", postal_code: "", locality: "", region: "" },
            });
            await sendPriceData(matchItem);
          }
        }
        setIsLoading(false); setApproved(true);
      } catch { setIsLoading(false); setApproved(false); }
    } else {
      try {
        setApproved(true);
        const { data } = await axios.post(`${marketUrl}/products/check`, {
          brand: matchItem.extracted_data.brand, model: activeItem.model, reference_number: activeItem.reference_number,
        });
        setFetchedData(data); setApproved(false); setIsOpen(true);
      } catch (error) {
        // Mirrors the branch above: `approved` was set true before the request,
        // so swallowing the failure left the row stuck in the approved state
        // with no dialog and no message.
        setApproved(false);
        showApiError(error, "check the market for this reference");
      }
    }
  };

  const onImport = async () => {
    if (fxBlocked) {
      alert.error(FX_BLOCKED_MSG);
      return;
    }
    try {
      setIsImporting(true); await approveMessage();
      const fileId = await uploadImage();
      // PUT is idempotent — re-clicking Import after a prior import updates the same
      // request (requestItem.id) on the backend rather than creating a duplicate.
      await axios.put(`/api/requests/${requestItem.id}/import-dao`, {
        product_id: activeItem.product_id, max_budget: Math.floor(rate * price * 100),
        message_id: matchItem.message_id, year, condition: [condition], type: requestItem?.type,
        request_type: requestType === requestTypes[0].id ? "sell" : "order",
        images: fileId ? [fileId] : [], delivery_set: deliverySet,
      });
      setImportLabel("Imported!"); setIsImporting(false);
      // Keep "Imported!" visible briefly before the parent removes this item from the list.
      setTimeout(() => { if (setIsRemoved) setIsRemoved(true); }, 1500);
    } catch { setImportLabel("Import"); setIsImporting(false); }
  };

  const onFetch = async () => {
    try {
      setIsFetching(true);
      const { data } = await axios.post(`${marketUrl}/fetch_matches`, {
        brand: matchItem.extracted_data.brand, model: compareLeftFormData.model,
        reference_number: compareLeftFormData.referenceNumber, dial_color: compareLeftFormData.dialColor,
      });
      setFetchedMatches(data.total_matches > 0 ? data.matches : [{}]);
      if (data.total_matches == 0) alert.show("No matches found");
      setIsFetched(true); setIsFetching(false);
    } catch { setIsFetching(false); setIsFetched(false); }
  };

  useEffect(() => {
    async function fetchData() {
      setApproved(false);
      const response = await axios.get(`/api/requests/get-request/${matchItem.message_id}`);
      if (response !== null) {
        setRequest(response.data);
        // Hydrate Approve from server-side messageStatus, but NOT Import — there is
        // no per-import flag, so the import button starts fresh on each open. The
        // import-dao PUT is idempotent (uses the same request_id), so re-clicking
        // updates the existing record on the backend.
        if (response.data.messageStatus === "approved") { setApproved(true); }
      }
    }
    if (matchItem.message_id) fetchData();
  }, [matchItem.message_id]);

  const productReference = matchItem?.matched_data?.reference_number || matchItem?.extracted_data?.reference_number;
  const compareSectionProps = {
    extractedData: { ...matchItem.extracted_data, reference_image_url: matchItem.whatsapp_image_url },
    matchedData: matchItem?.matched_data ? [matchItem.matched_data, ...matchItem.alternative_matches] : [],
    isNonMatch, fetchedMatches, setActiveItem, confidence: matchItem.match_confidence,
    productId, isFetched, setIsFetched, isRemoved, setCompareLeftFormData,
  };

  return (
    <div className="lm">
      <div className="lm__grid">
        {/* LEFT COLUMN — extracted above matched on desktop. Below 992px the
            two become a single swipeable row (see .lm__col--compare): the
            ticket asks for them "on the same row and be made swipeable", with
            matched revealed by swiping. */}
        <div className="lm__col lm__col--compare">
          <div className="lm__cell lm__cell--bordered">
            <CompareSection {...compareSectionProps} section="extracted" />
          </div>
          <div className="lm__cell lm__cell--bordered">
            <CompareSection {...compareSectionProps} section="matched" />
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="lm__col">
          <div className="lm__cell lm__cell--bordered">
            <Segmented
              fullWidth
              label="Message view"
              data-btn="extraction-message-view"
              options={[
                { value: "original", label: "Original message" },
                { value: "reply", label: "Auto reply" },
              ]}
              value={activeTab}
              onChange={setActiveTab}
            />
            <div className="lm__sender">
              <span className="lm__badge">
                <span className="lm__badge-icon-circle">
                  <img src="/assets/icons/icon-group.png" alt="" />
                </span>
                {matchItem.group_name}
              </span>
              <span className="lm__badge">
                <span className="lm__badge-icon-circle">
                  <img src="/assets/icons/icon-sender.png" alt="" />
                </span>
                {matchItem.sender_name}
              </span>
            </div>
            <div className="lm__sender-meta">
              <span>Phone: {matchItem.sender_phone}</span>
              <span>Date: {matchItem.message_date}</span>
            </div>
            <div className="lm__msg-area">
              {/* "Additional Details" heads the message body on BOTH tabs —
                  Figma node 495-6154 shows it above the Auto Reply body too.
                  It was previously rendered only on Original Message. */}
              <div className="lm__msg-label">Additional Details</div>
              {activeTab === "original" ? (
                <WhatsappMessageWithHighlighting messageText={messageText} productReference={productReference} onChange={setMessageText} />
              ) : (
                <>
                  <WhatsappMessageWithHighlighting messageText={preGeneratedMessageText} productReference={productReference} onChange={setPreGeneratedMessageText} />
                  <div className="lm__send-row">
                    <Button
                      href={`https://wa.me/${matchItem.sender_phone}?text=${encodeURIComponent(preGeneratedMessageText)}`}
                      target="_blank" rel="noopener noreferrer"
                      variant="secondary" size="lg" fullWidth
                      leadingIcon={<Icon name="MessageCircle" size="sm" />}
                      data-btn="extraction-send">
                      Send on WhatsApp
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="lm__cell lm__cell--bordered" style={activeTab !== "original" ? { display: "none" } : undefined}>
          <div className="lm__form-grid">
            <div className={fieldClass(deliverySet)}>
              <label>Delivery Set</label>
              <FieldGroup placeholder="Delivery Set" type="select" options={boxOptions} name="delivery_set" noFormik value={deliverySet} onChange={setDeliverySet} />
            </div>
            <div className={fieldClass(currency)}>
              <label>Currency</label>
              <FieldGroup placeholder="Currency" type="select" options={currencyOptions} name="currency" noFormik value={currency} onChange={setCurrency} />
            </div>
            <div className={fieldClass(year)}>
              <label>Year</label>
              <FieldGroup placeholder="Year" type="select" options={yearOptions} name="year" noFormik value={year} onChange={setYear} />
            </div>
            <div className={fieldClass(price)}>
              <label>Price</label>
              <FieldGroup placeholder="Price" name="price" type="numberFormat" value={price} onChange={setPrice} noFormik />
            </div>
            <div className={fieldClass(month)}>
              <label>Month</label>
              <FieldGroup placeholder="Month" type="select" options={monthOptions} name="month" noFormik value={month} onChange={setMonth} />
            </div>
            <div className={fieldClass(paymentType)}>
              <label>Payment Type</label>
              <FieldGroup placeholder="Payment Types" type="select" options={paymentTypeOptions} name="payment_type" noFormik value={paymentType} onChange={setPaymentType} />
            </div>
            <div className={fieldClass(condition)}>
              <label>Condition</label>
              <FieldGroup placeholder="Condition" type="select" options={conditionOptions} name="condition" noFormik value={condition} onChange={setCondition} />
            </div>
            <div className={fieldClass(location)}>
              <label>Country</label>
              <FieldGroup placeholder="Country" type="select" options={locationOptions} name="location" noFormik value={location} onChange={setLocation} />
            </div>
          </div>
          </div>{/* end lm__cell bottom-right */}
        </div>{/* end lm__col right */}
      </div>{/* end lm__grid */}

      {/* Footer */}
      <ButtonGroup equal className="lm__footer">
        <Button
          variant="primary" size="lg" data-btn="extraction-approve"
          onClick={onApprove}
          disabled={requestItem?.messageStatus === "approved" || approved || isEmpty(activeItem)}>
          {isLoading ? "Sending..." : (requestItem?.messageStatus === "approved" || approved) ? "Approved!" : "Approve"}
        </Button>
        {isNonMatch && (
          <Button variant="secondary" size="lg" data-btn="extraction-fetch" onClick={onFetch} disabled={isFetched}>
            {isFetching ? "Fetching..." : isFetched ? "Fetched" : "Fetch"}
          </Button>
        )}
        {requestType !== "Sell" && !hideImport && (
          <Button variant="secondary" size="lg" data-btn="extraction-import" onClick={onImport} disabled={!matchItem?.message_id}>
            {isImporting ? "Importing..." : (requestItem?.messageStatus === "approved" || importLabel === "Imported!") ? "Imported!" : "Import"}
          </Button>
        )}
      </ButtonGroup>

      <NewPopup source="whatsapp" checkedData={fetchedData} activeItem={activeItem}
        isOpen={isOpen} setIsOpen={setIsOpen} matchItem={matchItem} isNonMatch={isNonMatch}
        setIsFetched={setIsFetched} fetchNewData={fetchWhatsappData} formData={formData} requestType={requestType} />
    </div>
  );
};

export default WhatsappView;
